import { useTranslations } from "@/hooks/use-translations";
import {
	useNavigate,
	useLocation,
	useParams,
	Link,
	useSearchParams,
} from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { NotificationCenter } from "@/components/notifications/notification-center";
import {
	ArrowLeft,
	Search,
	Share2,
	FileEdit,
	BarChart3,
	TrendingUp,
	Rocket,
	ChevronDown,
	Copy,
	Check,
	Mail,
	Pencil,
	Loader2,
	X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { SocialPlatformModal } from "@/components/social-platform-modal";
import {
	IconChannelsOverview,
	IconCompetitors,
	IconContentPlan,
	IconCulturalAdaptation,
	IconFC,
	IconGoogleAds,
	IconInstagramAds,
	IconMarketOverview,
	IconMediaPlan,
	IconOffer,
	IconTargetPersonas,
} from "@/lib/studio-tools";

interface ProjectHeaderProps {
	projectTitle?: string;
	showBack?: boolean;
	onBack?: () => void;
	isCommentMode?: boolean;
	onCommentToggle?: () => void;
}

export function ProjectHeader({
	projectTitle = "Strategic Market Entry and Enterprise Growth Framework",
	showBack = true,
	onBack,
	isCommentMode = false,
	onCommentToggle,
}: ProjectHeaderProps) {
	const t = useTranslations("workspace");
	const navigate = useNavigate();
	const location = useLocation();
	const { projectId } = useParams<{ projectId: string }>();
	const queryClient = useQueryClient();
	const { i18n } = useTranslation();

	const [searchParams] = useSearchParams();
	const [appsOpen, setAppsOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [shareOpen, setShareOpen] = useState(false);
	const [linkCopied, setLinkCopied] = useState(false);
	const [socialModalOpen, setSocialModalOpen] = useState(false);
	const [selectedSocial, setSelectedSocial] = useState<string | null>(null);
	const [loadingTool, setLoadingTool] = useState<string | null>(null);
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editTitle, setEditTitle] = useState("");
	const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
	const appsRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLDivElement>(null);
	const shareRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const titleInputRef = useRef<HTMLInputElement>(null);

	const isOnWorkspace = location.pathname.startsWith("/workspace");
	const isOnCanvas = location.pathname.startsWith("/canvas");
	const gridTarget = isOnWorkspace
		? `/canvas/${projectId || "main"}`
		: isOnCanvas
			? `/workspace/${projectId || "main"}`
			: `/canvas/${projectId || "main"}`;

	const handleBack = onBack ?? (() => navigate(-1));

	const pid = projectId || "main";

	const appItems = [
		{
			icon: IconMarketOverview,
			label: "Market Overview",
			description: "Market overview",
			slug: "market-overview",
			path: `/canvas/${pid}?tool=market-overview`,
		},
		{
			icon: IconTargetPersonas,
			label: "Target Personas",
			description: "Target personas",
			slug: "target-personas",
			path: `/canvas/${pid}?tool=target-personas`,
		},
		{
			icon: IconCompetitors,
			label: "Competitors",
			description: "Competitor analysis",
			slug: "competitors",
			path: `/canvas/${pid}?tool=competitors`,
		},
		{
			icon: IconOffer,
			label: "Offer",
			description: "Offer analysis",
			slug: "offer-and-key-messages",
			path: `/canvas/${pid}?tool=offer-and-key-messages`,
		},
		{
			icon: IconCulturalAdaptation,
			label: "Cultural Adaptation",
			description: "Cultural adaptation analysis",
			slug: "cultural-adaptation",
			path: `/canvas/${pid}?tool=cultural-adaptation`,
		},
		{
			icon: IconChannelsOverview,
			label: "Channels Overview",
			description: "Channels overview",
			slug: "channels-overview",
			path: `/canvas/${pid}?tool=channels-overview`,
		},
		{
			icon: IconMediaPlan,
			label: "Media Planning",
			description: "Media planning",
			slug: "media-plan-and-budget",
			path: `/canvas/${pid}?tool=media-plan-and-budget`,
		},
		{
			icon: IconContentPlan,
			label: "Content Plan",
			description: "Content plan",
			slug: "content-plan",
			path: `/canvas/${pid}?tool=content-plan`,
			openSocialModal: true,
		},
		{
			icon: IconInstagramAds,
			label: "Instagram Ads",
			description: "Instagram ads",
			slug: undefined,
			path: `/canvas/${pid}`,
		},
		{
			icon: IconGoogleAds,
			label: "Google Ads",
			description: "Google ads",
			slug: undefined,
			path: `/canvas/${pid}`,
		},
	] as Array<{
		icon: IconFC;
		label: string;
		description: string;
		path: string;
		slug?: string;
		openSocialModal?: boolean;
	}>;

	// Close dropdown on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (appsRef.current && !appsRef.current.contains(e.target as Node)) {
				setAppsOpen(false);
			}
		}
		if (appsOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [appsOpen]);

	// Close search on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
				setSearchOpen(false);
			}
		}
		if (searchOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [searchOpen]);

	// Close share dropdown on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
				setShareOpen(false);
			}
		}
		if (shareOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [shareOpen]);

	// Auto-focus search input when opened
	useEffect(() => {
		if (searchOpen && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [searchOpen]);

	// Auto-focus title input when editing
	useEffect(() => {
		if (isEditingTitle && titleInputRef.current) {
			titleInputRef.current.focus();
			titleInputRef.current.select();
		}
	}, [isEditingTitle]);

	const shareUrl = typeof window !== "undefined" ? window.location.href : "";

	const handleSaveTitle = async () => {
		if (!editTitle.trim() || !projectId) return;

		setIsUpdatingTitle(true);
		try {
			await api.updateProjectTitle(projectId, { title: editTitle.trim() });
			queryClient.invalidateQueries({
				queryKey: ["projectDetails", projectId],
			});
			queryClient.invalidateQueries({ queryKey: ["projects"] });
			setIsEditingTitle(false);
			setEditTitle("");
			toast.success("Title updated successfully");
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to update title",
			);
		} finally {
			setIsUpdatingTitle(false);
		}
	};

	const handleToolClick = (item: (typeof appItems)[0]) => {
		setAppsOpen(false);

		if (item.openSocialModal) {
			setSelectedSocial(null);
			setSocialModalOpen(true);
			return;
		}

		navigate(item.path);
	};

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setLinkCopied(true);
			setTimeout(() => setLinkCopied(false), 2000);
		} catch {
			// fallback
		}
	};

	const handleShareEmail = () => {
		window.open(
			`mailto:?subject=${encodeURIComponent(projectTitle)}&body=${encodeURIComponent(shareUrl)}`,
		);
		setShareOpen(false);
	};

	const handleShareTelegram = () => {
		window.open(
			`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(projectTitle)}`,
			"_blank",
		);
		setShareOpen(false);
	};

	const handleShareWhatsApp = () => {
		window.open(
			`https://wa.me/?text=${encodeURIComponent(projectTitle + " " + shareUrl)}`,
			"_blank",
		);
		setShareOpen(false);
	};

	return (
		<>
			<motion.header
				initial={{ opacity: 0, y: -8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.25 }}
				className="flex items-center justify-between px-5 py-3 border-b border-border-subtle bg-bg-surface flex-shrink-0"
			>
				<div className="flex items-center gap-3 min-w-0">
					{showBack && (
						<Button variant="ghost" size="icon-sm" onClick={handleBack}>
							<ArrowLeft size={18} />
						</Button>
					)}
					<button
						onClick={() => navigate("/projects")}
						className="p-0 bg-transparent border-none cursor-pointer"
					>
						<img
							src="/logo.jpg"
							alt="Minora"
							className="w-8 h-8 rounded-full flex-shrink-0"
						/>
					</button>
					{isEditingTitle ? (
						<div className="flex items-center gap-2">
							<Input
								ref={titleInputRef}
								value={editTitle}
								onChange={(e) => setEditTitle(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && editTitle.trim()) {
										handleSaveTitle();
									} else if (e.key === "Escape") {
										setIsEditingTitle(false);
										setEditTitle("");
									}
								}}
								disabled={isUpdatingTitle}
								className="w-[300px] h-8 bg-bg-sunken border-border-control text-text-primary text-xl font-semibold"
							/>
							<Button
								variant="secondary"
								size="icon-sm"
								onClick={handleSaveTitle}
								disabled={isUpdatingTitle || !editTitle.trim()}
							>
								{isUpdatingTitle ? (
									<Loader2 size={16} className="animate-spin" />
								) : (
									<Check size={16} />
								)}
							</Button>
							<Button
								variant="ghost"
								size="icon-sm"
								onClick={() => {
									setIsEditingTitle(false);
									setEditTitle("");
								}}
								disabled={isUpdatingTitle}
							>
								<X size={16} />
							</Button>
						</div>
					) : (
						<div className="flex items-center gap-2 group">
							<h1 className="text-xl font-semibold text-text-primary truncate">
								{projectTitle}
							</h1>
							<Button
								variant="ghost"
								size="icon-sm"
								onClick={() => {
									setEditTitle(projectTitle);
									setIsEditingTitle(true);
								}}
								className="opacity-0 group-hover:opacity-100 transition-all"
								title="Edit title"
							>
								<Pencil size={16} />
							</Button>
						</div>
					)}
				</div>

				<div className="flex items-center gap-1.5 flex-shrink-0">
					<NotificationCenter />
					{/* Apps dropdown */}
					<div ref={appsRef} className="relative">
						<button
							onClick={() => setAppsOpen((v) => !v)}
							className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-lg font-medium text-text-primary transition-colors border ${appsOpen ? "border-border-subtle bg-bg-sunken" : "border-border-subtle"}`}
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_project_header_apps)">
									<path
										d="M17.1075 3.51099L12.085 0.610988C11.4512 0.246506 10.7328 0.0546875 10.0017 0.0546875C9.27052 0.0546875 8.55216 0.246506 7.91833 0.610988L2.89583 3.51099C2.26323 3.87758 1.73789 4.40376 1.37231 5.03695C1.00673 5.67014 0.813697 6.38817 0.8125 7.11932V12.9193C0.813719 13.6506 1.00675 14.3687 1.37233 15.002C1.7379 15.6354 2.26322 16.1617 2.89583 16.5285L7.91833 19.4277C8.55208 19.7924 9.27047 19.9843 10.0017 19.9843C10.7329 19.9843 11.4513 19.7924 12.085 19.4277L17.1075 16.5285C17.7401 16.1617 18.2654 15.6354 18.631 15.002C18.9966 14.3687 19.1896 13.6506 19.1908 12.9193V7.11932C19.1896 6.38817 18.9966 5.67014 18.631 5.03695C18.2654 4.40376 17.7401 3.87758 17.1075 3.51099ZM8.75167 2.05516C9.13171 1.83573 9.56283 1.72022 10.0017 1.72022C10.4405 1.72022 10.8716 1.83573 11.2517 2.05516L16.2742 4.95432C16.4015 5.03482 16.5212 5.12688 16.6317 5.22932L11.4708 8.20849C11.024 8.46606 10.5174 8.60164 10.0017 8.60164C9.48595 8.60164 8.97929 8.46606 8.5325 8.20849L3.37167 5.22932C3.48214 5.12688 3.60181 5.03482 3.72917 4.95432L8.75167 2.05516ZM3.72917 15.0843C3.34934 14.8642 3.03396 14.5483 2.81459 14.1681C2.59523 13.7878 2.47956 13.3566 2.47917 12.9177V7.11932C2.48495 6.96919 2.50476 6.81993 2.53833 6.67349L7.69917 9.65266C8.15495 9.91192 8.65191 10.0909 9.16833 10.1818V18.156C9.02439 18.1119 8.88489 18.0544 8.75167 17.9843L3.72917 15.0843ZM17.5242 12.9177C17.5238 13.3566 17.4081 13.7878 17.1887 14.1681C16.9694 14.5483 16.654 14.8642 16.2742 15.0843L11.2517 17.9843C11.1184 18.0544 10.9789 18.1119 10.835 18.156V10.1818C11.3514 10.0909 11.8484 9.91192 12.3042 9.65266L17.465 6.67349C17.4986 6.81993 17.5184 6.96919 17.5242 7.11932V12.9177Z"
										fill="currentColor"
									/>
								</g>
								<defs>
									<clipPath id="clip0_project_header_apps">
										<rect width="20" height="20" fill="white" />
									</clipPath>
								</defs>
							</svg>
							{t("apps")}
							<ChevronDown
								size={14}
								className={`transition-transform duration-200 ${appsOpen ? "rotate-180" : ""}`}
							/>
						</button>

						<AnimatePresence>
							{appsOpen && (
								<motion.div
									initial={{ opacity: 0, y: 8, scale: 0.96 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: 8, scale: 0.96 }}
									transition={{ duration: 0.18, ease: "easeOut" }}
									className="absolute right-0 top-full mt-2 w-[280px] rounded-xl border border-border-subtle bg-bg-surface backdrop-blur-xl shadow-popover z-50 overflow-hidden"
								>
									<div className="px-3 py-2.5 border-b border-border-subtle">
										<p className="text-2xs font-semibold text-text-tertiary uppercase tracking-wider">
											{t("apps")}
										</p>
									</div>
									<div className="py-1.5 px-1.5">
										{appItems.map((item) => {
											// Normalize paths for comparison
											const currentPath = location.pathname;
											const pathUrl = new URL(
												item.path,
												window.location.origin,
											);
											const isPathMatch = currentPath === pathUrl.pathname;
											const isToolMatch =
												searchParams.get("tool") === (item.slug || null);

											// it is active if path matches and (either it's a tool-specific path or no tool is active)
											// only tools with slugs can be marked as active in this dropdown
											const isActive =
												isPathMatch && isToolMatch && !!item.slug;
											const isModalItem = item.openSocialModal;

											const content = (
												<>
													<div
														className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
															isActive
																? "bg-bg-surface text-text-primary"
																: "bg-bg-canvas text-text-tertiary group-hover:bg-bg-sunken group-hover:text-text-secondary"
														}`}
													>
														<item.icon className="w-3.5 h-3.5" />
													</div>
													<div className="min-w-0">
														<p className="text-sm font-medium leading-tight truncate">
															{item.label}
														</p>
														<p className="text-2xs text-text-tertiary leading-tight truncate">
															{item.description}
														</p>
													</div>
													{isActive && item.slug && (
														<div className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
													)}
												</>
											);

											const className = `w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl transition-colors group ${
												isActive
													? "bg-nav-active-bg text-text-primary"
													: "text-text-secondary hover:bg-bg-canvas hover:text-text-primary"
											}`;

											if (isModalItem) {
												return (
													<button
														key={item.label}
														onClick={() => handleToolClick(item)}
														className={className}
													>
														{content}
													</button>
												);
											}

											return (
												<Link
													key={item.label}
													to={item.path}
													onClick={() => setAppsOpen(false)}
													className={className}
												>
													{content}
												</Link>
											);
										})}
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</div>
					<button
						onClick={onCommentToggle}
						className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-lg font-medium transition-colors border ${
							isCommentMode
								? "border-brand-700 bg-nav-active-bg text-text-primary"
								: "border-border-subtle text-text-primary"
						}`}
					>
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clipPath="url(#clip0_842_6398)">
								<path
									d="M11.2492 9.99887C11.2492 10.6889 10.6892 11.2489 9.9992 11.2489C9.3092 11.2489 8.7492 10.6889 8.7492 9.99887C8.7492 9.30887 9.3092 8.74887 9.9992 8.74887C10.6892 8.74887 11.2492 9.30887 11.2492 9.99887ZM14.1659 8.74887C13.4759 8.74887 12.9159 9.30887 12.9159 9.99887C12.9159 10.6889 13.4759 11.2489 14.1659 11.2489C14.8559 11.2489 15.4159 10.6889 15.4159 9.99887C15.4159 9.30887 14.8559 8.74887 14.1659 8.74887ZM5.83253 8.74887C5.14253 8.74887 4.58253 9.30887 4.58253 9.99887C4.58253 10.6889 5.14253 11.2489 5.83253 11.2489C6.52253 11.2489 7.08253 10.6889 7.08253 9.99887C7.08253 9.30887 6.52253 8.74887 5.83253 8.74887ZM19.9992 10.2822V15.8322C19.9992 18.1297 18.13 19.9989 15.8325 19.9989H10.9017C5.06753 19.9989 0.390862 16.0055 0.0241958 10.7105C-0.176638 7.81387 0.880029 4.97887 2.92336 2.93303C4.9667 0.8872 7.8017 -0.1728 10.6959 0.0230331C15.9125 0.3772 19.9992 4.88303 19.9992 10.2822ZM18.3325 10.2822C18.3325 5.75637 14.9292 1.98053 10.5834 1.68553C10.3925 1.6722 10.2017 1.66637 10.0117 1.66637C7.79836 1.66637 5.67086 2.5397 4.10253 4.11053C2.3992 5.81553 1.51836 8.1797 1.6867 10.5955C1.99753 15.0789 5.87336 18.333 10.9025 18.333H15.8334C17.2117 18.333 18.3334 17.2114 18.3334 15.833V10.283L18.3325 10.2822Z"
									fill="currentColor"
								/>
							</g>
							<defs>
								<clipPath id="clip0_842_6398">
									<rect width="20" height="20" fill="white" />
								</clipPath>
							</defs>
						</svg>

						{t("comment")}
					</button>
					<div ref={shareRef} className="relative">
						<button
							onClick={() => setShareOpen((v) => !v)}
							className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-lg font-medium transition-colors border ${
								shareOpen
									? "border-border-subtle bg-bg-sunken text-text-primary"
									: "border-border-subtle text-text-primary"
							}`}
						>
							<Share2 size={16} />
							{t("share")}
						</button>

						<AnimatePresence>
							{shareOpen && (
								<motion.div
									initial={{ opacity: 0, y: 8, scale: 0.96 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: 8, scale: 0.96 }}
									transition={{ duration: 0.18, ease: "easeOut" }}
									className="absolute right-0 top-full mt-2 w-[300px] rounded-xl border border-border-subtle bg-bg-surface backdrop-blur-xl shadow-popover z-50 overflow-hidden"
								>
									<div className="px-4 py-3 border-b border-border-subtle">
										<p className="text-2xs font-semibold text-text-tertiary uppercase tracking-wider">
											Share project
										</p>
									</div>
									{/* Share options */}
									<div className="px-2 py-2">
										<button
											onClick={handleShareEmail}
											className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl text-text-secondary hover:bg-bg-canvas hover:text-text-primary transition-colors group"
										>
											<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg-canvas text-text-tertiary group-hover:bg-bg-sunken group-hover:text-text-secondary transition-colors">
												<Mail size={14} />
											</div>
											<span className="text-sm font-medium">Email</span>
										</button>
										<button
											onClick={handleShareTelegram}
											className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl text-text-secondary hover:bg-bg-canvas hover:text-text-primary transition-colors group"
										>
											<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg-canvas text-text-tertiary group-hover:bg-bg-sunken group-hover:text-text-secondary transition-colors">
												<svg
													width="14"
													height="14"
													viewBox="0 0 24 24"
													fill="currentColor"
												>
													<path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
												</svg>
											</div>
											<span className="text-sm font-medium">Telegram</span>
										</button>
										<button
											onClick={handleShareWhatsApp}
											className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl text-text-secondary hover:bg-bg-canvas hover:text-text-primary transition-colors group"
										>
											<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg-canvas text-text-tertiary group-hover:bg-bg-sunken group-hover:text-text-secondary transition-colors">
												<svg
													width="14"
													height="14"
													viewBox="0 0 24 24"
													fill="currentColor"
												>
													<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
												</svg>
											</div>
											<span className="text-sm font-medium">WhatsApp</span>
										</button>
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</div>
					<div ref={searchRef} className="relative flex items-center">
						<AnimatePresence>
							{searchOpen && (
								<motion.div
									initial={{ width: 0, opacity: 0 }}
									animate={{ width: 200, opacity: 1 }}
									exit={{ width: 0, opacity: 0 }}
									transition={{ duration: 0.2, ease: "easeOut" }}
									className="overflow-hidden mr-1"
								>
									<Input
										ref={searchInputRef}
										type="text"
										placeholder="Search..."
										className="h-8 py-1"
									/>
								</motion.div>
							)}
						</AnimatePresence>
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={() => setSearchOpen((v) => !v)}
							className={cn(
								"text-text-primary",
								searchOpen && "bg-bg-sunken",
							)}
						>
							<Search size={16} />
						</Button>
					</div>
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={() => navigate(gridTarget)}
						className="text-text-primary"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clipPath="url(#clip0_project_header_grid)">
								<path
									d="M5.83333 0H3.33333C2.44928 0 1.60143 0.35119 0.976311 0.976311C0.35119 1.60143 0 2.44928 0 3.33333L0 5.83333C0 6.71739 0.35119 7.56524 0.976311 8.19036C1.60143 8.81548 2.44928 9.16667 3.33333 9.16667H5.83333C6.71739 9.16667 7.56524 8.81548 8.19036 8.19036C8.81548 7.56524 9.16667 6.71739 9.16667 5.83333V3.33333C9.16667 2.44928 8.81548 1.60143 8.19036 0.976311C7.56524 0.35119 6.71739 0 5.83333 0ZM7.5 5.83333C7.5 6.27536 7.32441 6.69928 7.01184 7.01184C6.69928 7.32441 6.27536 7.5 5.83333 7.5H3.33333C2.89131 7.5 2.46738 7.32441 2.15482 7.01184C1.84226 6.69928 1.66667 6.27536 1.66667 5.83333V3.33333C1.66667 2.89131 1.84226 2.46738 2.15482 2.15482C2.46738 1.84226 2.89131 1.66667 3.33333 1.66667H5.83333C6.27536 1.66667 6.69928 1.84226 7.01184 2.15482C7.32441 2.46738 7.5 2.89131 7.5 3.33333V5.83333Z"
									fill="currentColor"
								/>
								<path
									d="M16.6693 0H14.1693C13.2852 0 12.4374 0.35119 11.8122 0.976311C11.1871 1.60143 10.8359 2.44928 10.8359 3.33333V5.83333C10.8359 6.71739 11.1871 7.56524 11.8122 8.19036C12.4374 8.81548 13.2852 9.16667 14.1693 9.16667H16.6693C17.5533 9.16667 18.4012 8.81548 19.0263 8.19036C19.6514 7.56524 20.0026 6.71739 20.0026 5.83333V3.33333C20.0026 2.44928 19.6514 1.60143 19.0263 0.976311C18.4012 0.35119 17.5533 0 16.6693 0ZM18.3359 5.83333C18.3359 6.27536 18.1603 6.69928 17.8478 7.01184C17.5352 7.32441 17.1113 7.5 16.6693 7.5H14.1693C13.7272 7.5 13.3033 7.32441 12.9908 7.01184C12.6782 6.69928 12.5026 6.27536 12.5026 5.83333V3.33333C12.5026 2.89131 12.6782 2.46738 12.9908 2.15482C13.3033 1.84226 13.7272 1.66667 14.1693 1.66667H16.6693C17.1113 1.66667 17.5352 1.84226 17.8478 2.15482C18.1603 2.46738 18.3359 2.89131 18.3359 3.33333V5.83333Z"
									fill="currentColor"
								/>
								<path
									d="M5.83333 10.8359H3.33333C2.44928 10.8359 1.60143 11.1871 0.976311 11.8122C0.35119 12.4374 0 13.2852 0 14.1693L0 16.6693C0 17.5533 0.35119 18.4012 0.976311 19.0263C1.60143 19.6514 2.44928 20.0026 3.33333 20.0026H5.83333C6.71739 20.0026 7.56524 19.6514 8.19036 19.0263C8.81548 18.4012 9.16667 17.5533 9.16667 16.6693V14.1693C9.16667 13.2852 8.81548 12.4374 8.19036 11.8122C7.56524 11.1871 6.71739 10.8359 5.83333 10.8359ZM7.5 16.6693C7.5 17.1113 7.32441 17.5352 7.01184 17.8478C6.69928 18.1603 6.27536 18.3359 5.83333 18.3359H3.33333C2.89131 18.3359 2.46738 18.1603 2.15482 17.8478C1.84226 17.5352 1.66667 17.1113 1.66667 16.6693V14.1693C1.66667 13.7272 1.84226 13.3033 2.15482 12.9908C2.46738 12.6782 2.89131 12.5026 3.33333 12.5026H5.83333C6.27536 12.5026 6.69928 12.6782 7.01184 12.9908C7.32441 13.3033 7.5 13.7272 7.5 14.1693V16.6693Z"
									fill="currentColor"
								/>
								<path
									d="M16.6693 10.8359H14.1693C13.2852 10.8359 12.4374 11.1871 11.8122 11.8122C11.1871 12.4374 10.8359 13.2852 10.8359 14.1693V16.6693C10.8359 17.5533 11.1871 18.4012 11.8122 19.0263C12.4374 19.6514 13.2852 20.0026 14.1693 20.0026H16.6693C17.5533 20.0026 18.4012 19.6514 19.0263 19.0263C19.6514 18.4012 20.0026 17.5533 20.0026 16.6693V14.1693C20.0026 13.2852 19.6514 12.4374 19.0263 11.8122C18.4012 11.1871 17.5533 10.8359 16.6693 10.8359ZM18.3359 16.6693C18.3359 17.1113 18.1603 17.5352 17.8478 17.8478C17.5352 18.1603 17.1113 18.3359 16.6693 18.3359H14.1693C13.7272 18.3359 13.3033 18.1603 12.9908 17.8478C12.6782 17.5352 12.5026 17.1113 12.5026 16.6693V14.1693C12.5026 13.7272 12.6782 13.3033 12.9908 12.9908C13.3033 12.6782 13.7272 12.5026 14.1693 12.5026H16.6693C17.1113 12.5026 17.5352 12.6782 17.8478 12.9908C18.1603 13.3033 18.3359 13.7272 18.3359 14.1693V16.6693Z"
									fill="currentColor"
								/>
							</g>
							<defs>
								<clipPath id="clip0_project_header_grid">
									<rect width="20" height="20" fill="white" />
								</clipPath>
							</defs>
						</svg>
					</Button>
					<div className="w-8 h-8 rounded-full bg-brand-400 flex items-center justify-center text-text-on-brand text-xs cursor-pointer font-bold ml-1">
						T
					</div>
				</div>
			</motion.header>

			<SocialPlatformModal
				open={socialModalOpen}
				onClose={() => setSocialModalOpen(false)}
				selected={selectedSocial}
				onSelect={(platformId) => setSelectedSocial(platformId || null)}
				continueDisabled={!!loadingTool}
				onContinue={async () => {
					if (!selectedSocial || !projectId) return;
					const platform = selectedSocial;
					setSocialModalOpen(false);
					setLoadingTool("content-plan");
					try {
						await api.runCanvasTool(
							"content-plan",
							projectId,
							(i18n.resolvedLanguage ?? i18n.language ?? "ru") as
								| "en"
								| "ru"
								| "uz",
							platform,
						);
						toast.success("Content plan started");
						// The plan is already running — open the canvas without
						// `?tool=content-plan`, which would ask for a platform again.
						if (!isOnCanvas) {
							navigate(`/canvas/${projectId}`);
						}
						queryClient.invalidateQueries({
							queryKey: ["toolResults", projectId],
						});
					} catch (error) {
						toast.error(
							error instanceof Error
								? error.message
								: "Failed to start content plan",
						);
					} finally {
						setLoadingTool(null);
						setSelectedSocial(null);
					}
				}}
			/>
		</>
	);
}
